import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faBars,
	faCalendar,
	faCaretSquareDown,
	faChevronDown,
	faChevronRight,
	faCircle,
	faClock,
	faDollarSign,
	faEllipsis,
	faEnvelope,
	faFileAlt,
	faFont,
	faHashtag,
	faIcons,
	faImage,
	faLink,
	faPercent,
	faPhone,
	faPuzzlePiece,
	faSquare,
	faStar,
	faTag,
	faTags,
	faThumbtack,
	faVolumeUp,
} from '@fortawesome/pro-solid-svg-icons';
import { getListRowContext } from '@supply-mover-2-0/core';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { useTranslation } from '../../../../providers/TranslationProvider';
import { cn } from '../../../../utils/cn';
import type { CustomComponent } from '../../../registry/ComponentRegistry';
import {
	groupComponentsByCategory,
	useComponentRegistry,
} from '../../../registry/ComponentRegistry';
import type { DisplayStyle, FieldConfiguration } from '../../../types';
import { useListBuilderContextSafe } from '../../core/ListBuilderProvider';
import { FieldPreview } from './FieldPreview';

/** A category plus its components, already ordered for display. */
interface ComponentCategory {
	name: string;
	components: Array<CustomComponent>;
}

/**
 * Group components by category and order them for the picker: categories A→Z
 * with the catch-all "Other" pinned last, and components A→Z inside each one.
 */
function toSortedCategories(
	components: Array<CustomComponent>,
): Array<ComponentCategory> {
	const grouped = groupComponentsByCategory(components);

	return Object.keys(grouped)
		.sort((a, b) => {
			if (a === 'Other' && b !== 'Other') {
				return 1;
			}
			if (b === 'Other' && a !== 'Other') {
				return -1;
			}
			return a.localeCompare(b);
		})
		.map((name) => ({
			name,
			components: [...(grouped[name] ?? [])].sort((a, b) =>
				a.displayName.localeCompare(b.displayName),
			),
		}));
}

interface DisplayStyleSelectorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	previewValue?: string;
	configuration?: any;
}

// Display style options with icons
const displayStyles: Array<{
	value: DisplayStyle;
	label: string;
	icon: IconDefinition;
}> = [
	{ value: 'text', label: 'Text', icon: faFont },
	{ value: 'badge', label: 'Badge', icon: faTag },
	{ value: 'badge_list', label: 'Badge List', icon: faTags },
	{ value: 'pill', label: 'Pill', icon: faEllipsis },
	{ value: 'chip', label: 'Chip', icon: faTag },
	{ value: 'dot', label: 'Dot', icon: faCircle },
	{ value: 'icon', label: 'Icon', icon: faIcons },
	{ value: 'icon_text', label: 'Icon + Text', icon: faFileAlt },
	{ value: 'phone', label: 'Phone', icon: faPhone },
	{ value: 'email', label: 'Email', icon: faEnvelope },
	{ value: 'date', label: 'Date', icon: faCalendar },
	{ value: 'relative_date', label: 'Relative Date', icon: faClock },
	{ value: 'number', label: 'Number', icon: faHashtag },
	{ value: 'currency', label: 'Currency', icon: faDollarSign },
	{ value: 'percent', label: 'Percent', icon: faPercent },
	{ value: 'image', label: 'Image', icon: faImage },
	{ value: 'button', label: 'Button', icon: faSquare },
	{ value: 'audio_player', label: 'Audio Player', icon: faVolumeUp },
	{
		value: 'actions_dropdown',
		label: 'Actions Dropdown',
		icon: faCaretSquareDown,
	},
	{ value: 'related_entity_list', label: 'Related Entities', icon: faLink },
	{ value: 'pin_toggle', label: 'Pin Toggle', icon: faThumbtack },
	{
		value: 'primary_contact_toggle',
		label: 'Primary Contact Toggle',
		icon: faStar,
	},
	{ value: 'line_break', label: 'Line Break', icon: faBars },
];

export function DisplayStyleSelector({
	field,
	onUpdateField,
	previewValue,
	configuration,
}: DisplayStyleSelectorProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const { components: customComponents, splitComponentsByContext } =
		useComponentRegistry();

	// PD-16145 — the picker is scoped to the list being edited. `listKey` comes
	// from the list-builder provider; the "safe" hook is used because the field
	// configurator also renders outside a provider, where the right answer is
	// to fall back to offering everything rather than to throw.
	const listBuilderContext = useListBuilderContextSafe();
	const rowContext = getListRowContext(listBuilderContext?.listKey);

	const [showOtherComponents, setShowOtherComponents] = useState(false);

	const { relevantCategories, otherCategories, otherComponentCount } = useMemo(
		() => {
			const { inContext, outOfContext } = splitComponentsByContext(rowContext);

			// A component already chosen for this field is always offered up
			// front, even when it is out of scope for this list — otherwise an
			// existing configuration looks like it lost its selection.
			const selectedId =
				field.displayStyle === 'custom' ? field.customComponent?.id : undefined;
			const promoted = outOfContext.filter(
				(component) => component.id === selectedId,
			);
			const remaining = outOfContext.filter(
				(component) => component.id !== selectedId,
			);

			return {
				relevantCategories: toSortedCategories([...inContext, ...promoted]),
				otherCategories: toSortedCategories(remaining),
				otherComponentCount: remaining.length,
			};
		},
		// `splitComponentsByContext` reads the registry singleton, and
		// `customComponents` is what changes when a module registers late —
		// depending on it is what makes this recompute.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			customComponents,
			rowContext,
			field.displayStyle,
			field.customComponent?.id,
		],
	);

	const isSelectedComponent = (componentId: string): boolean =>
		field.displayStyle === 'custom' &&
		field.customComponent?.id === componentId;

	const renderCategory = (category: ComponentCategory): React.JSX.Element => (
		<div key={category.name} className="w-full min-w-0 mt-3">
			<Text
				as="div"
				className="text-[10px] font-semibold uppercase tracking-wide mb-1 opacity-70"
				color="secondary"
			>
				{t(category.name)}
			</Text>
			<div className="flex flex-wrap gap-2 min-w-0">
				{category.components.map((component) => (
					<button
						key={component.id}
						onClick={() => {
							onUpdateField({
								displayStyle: 'custom' as DisplayStyle,
								customComponent: {
									id: component.id,
								},
							});
						}}
						// `max-w-full` + the label's `min-w-0 break-words` below let a
						// long component name wrap onto a second line inside the
						// button. Without them the label is an unshrinkable flex item,
						// so the button grows to the full one-line width of its text
						// and overflows the panel instead of wrapping.
						className={cn(
							'flex items-center px-3 py-2 border-2 rounded-lg min-w-[100px] max-w-full text-left',
							isSelectedComponent(component.id)
								? cn(
										colors.classes.controlSelected,
										colors.classes.borderPrimary,
									)
								: cn(
										colors.classes.bgPrimary,
										colors.classes.borderDefault,
										colors.classes.textSecondary,
										colors.classes.bgPrimaryHover,
									),
						)}
					>
						<Icon
							icon={component.icon ?? faPuzzlePiece}
							size="sm"
							color={
								isSelectedComponent(component.id)
									? colors.values.primaryDark
									: colors.values.textSecondary
							}
							className="mr-1.5 shrink-0"
						/>
						<Text
							className={cn(
								'text-xs min-w-0 break-words',
								isSelectedComponent(component.id)
									? 'font-semibold'
									: 'font-medium',
							)}
						>
							{t(component.displayName)}
						</Text>
					</button>
				))}
			</div>
		</div>
	);

	const handleStyleSelect = (style: (typeof displayStyles)[0]) => {
		if (style.value === 'line_break') {
			// For line breaks, clear data and style configs
			onUpdateField({
				displayStyle: style.value,
				dataConfig: {
					type: 'custom_text',
					customText: '',
				},
				styleConfig: {
					size: 'md',
					colorRule: {
						type: 'static',
						staticColor: {
							bg: 'transparent',
							text: colors.values.textSecondary,
						},
					},
				},
			});
		} else if (
			style.value === 'number' ||
			style.value === 'currency' ||
			style.value === 'percent'
		) {
			// Currency defaults to 'decimal' (fixed 2 decimal places),
			// number and percent default to 'auto' (smart formatting)
			const defaultFormat = style.value === 'currency' ? 'decimal' : 'auto';
			onUpdateField({
				displayStyle: style.value,
				styleConfig: {
					// size default matches the line_break branch above for
					// fields whose config predates styleConfig.
					size: 'md',
					...field.styleConfig,
					formatOptions: {
						...field.styleConfig?.formatOptions,
						numberFormat:
							field.styleConfig?.formatOptions?.numberFormat ?? defaultFormat,
					},
				},
			});
		} else {
			onUpdateField({ displayStyle: style.value });
		}
	};

	return (
		<Card className="mb-4">
			<div className="p-4">
				<Text className="text-base font-semibold mb-3">Display Style</Text>

				<div className="flex flex-wrap gap-2">
					{displayStyles.map((style) => (
						<button
							key={style.value}
							onClick={() => {
								handleStyleSelect(style);
							}}
							className={cn(
								'flex items-center px-3 py-2 border-2 rounded-lg min-w-[100px]',
								field.displayStyle === style.value
									? cn(
											colors.classes.controlSelected,
											colors.classes.borderPrimary,
										)
									: cn(
											colors.classes.bgPrimary,
											colors.classes.borderDefault,
											colors.classes.textSecondary,
											colors.classes.bgPrimaryHover,
										),
							)}
						>
							<Icon
								icon={style.icon}
								size="sm"
								color={
									field.displayStyle === style.value
										? colors.values.primaryDark
										: colors.values.textSecondary
								}
								className="mr-1.5"
							/>
							<Text
								className={cn(
									'text-xs',
									field.displayStyle === style.value
										? 'font-semibold'
										: 'font-medium',
								)}
							>
								{style.label}
							</Text>
						</button>
					))}

					{/* Custom Components Section (PD-16145) — scoped to the list being
					    edited. Components built for THIS list's rows are grouped by
					    category and shown expanded; everything else moves into one
					    collapsed disclosure at the bottom so it stays reachable without
					    burying the components that apply. Built-in styles above keep
					    their hand-curated order.

					    Each category lives inside its own block-level wrapper with a
					    nested `flex flex-wrap` row so long category lists wrap inside the
					    modal instead of overflowing it. The previous structure relied on
					    inline `<Text>` headers with `w-full` to force flex line breaks; on
					    narrower modal widths that produced a horizontal scrollbar because
					    adjacent buttons couldn't reflow across category boundaries. */}
					{customComponents.length > 0 && (
						// `min-w-0` here is load-bearing: as a flex child of the outer
						// `flex flex-wrap` above, this wrapper would otherwise inherit
						// `min-width: auto`, which in flex resolves to the wrapper's
						// min-content — i.e. the widest non-wrappable thing inside.
						// The buttons further down have `min-w-[100px]` and won't
						// shrink, so without `min-w-0` the wrapper insists on being
						// wide enough to lay them all out un-wrapped, pushing the
						// modal content past its container and triggering a horizontal
						// scrollbar. `min-w-0` lets it shrink to its row's width so
						// the inner `flex flex-wrap` rows can actually wrap.
						<div className="w-full min-w-0 mt-2">
							<div
								className={cn('w-full h-px mb-2', colors.classes.borderDefault)}
							/>
							<Text
								as="div"
								className="text-xs font-semibold mb-2"
								color="secondary"
							>
								{t('Custom Components')}
							</Text>

							{relevantCategories.map(renderCategory)}

							{otherComponentCount > 0 && (
								<div className="w-full min-w-0 mt-3">
									<button
										type="button"
										aria-expanded={showOtherComponents}
										onClick={() => {
											setShowOtherComponents((shown) => !shown);
										}}
										className="flex items-center gap-1.5 min-w-0 max-w-full text-left py-1"
									>
										<Icon
											icon={
												showOtherComponents ? faChevronDown : faChevronRight
											}
											size="sm"
											color={colors.values.textSecondary}
										/>
										<Text
											className="text-[10px] font-semibold uppercase tracking-wide min-w-0 break-words"
											color="secondary"
										>
											{t('Components for other lists')} ({otherComponentCount})
										</Text>
									</button>

									{showOtherComponents && otherCategories.map(renderCategory)}
								</div>
							)}
						</div>
					)}
				</div>

				{/* Live Preview */}
				{field.displayStyle !== 'line_break' && (
					<FieldPreview
						field={field}
						previewValue={previewValue}
						configuration={configuration}
					/>
				)}
			</div>
		</Card>
	);
}
